import { Fragment, useMemo } from "react";

import { Dialog } from "@/components/ui/Dialog";
import { Kbd } from "@/components/ui/Kbd";
import type { NavGroup } from "@/app/nav";
import { commandKey, useShortcutSheet } from "@/lib/shortcuts";

/** A word between two keys: "then" for one after the other, "or" for either. */
interface Word {
  say: string;
}

const THEN: Word = { say: "then" };
const OR: Word = { say: "or" };

interface Row {
  label: string;
  /** The keys in the order they are shown, with any word that joins them. */
  keys: readonly (string | Word)[];
}

interface Section {
  heading: string;
  /** What has to be true first: where the keyboard must be. */
  note?: string;
  rows: Row[];
}

/** The queues where A and D decide the request the keyboard is on. */
const QUEUES = ["/admin/leaves", "/admin/letters", "/admin/change-requests"];
/** The screens where HR keeps notes against a record. */
const NOTED = ["/admin/employees", "/admin/leaves"];

/**
 * Every shortcut there is, for the person looking at it.
 *
 * Written from the code that listens for each key, not from memory: the
 * shell and the search box for the first three sections, `DataTable` for
 * the lists and the queues, `AreaChart`, `Select`, the message box and the
 * note box for the rest. A key added there belongs here as well.
 */
function sectionsFor(nav: NavGroup[], canSearchPeople: boolean): Section[] {
  const items = nav.flatMap((group) => group.items);
  const sections: Section[] = [
    {
      heading: "Anywhere",
      rows: [
        {
          label: canSearchPeople ? "Search people and screens" : "Search screens",
          keys: [commandKey(), "K", OR, "/"],
        },
        { label: "Fold or open the sidebar", keys: ["["] },
        { label: "Show this list", keys: ["?"] },
        { label: "Close a dialog, a panel or a menu", keys: ["Esc"] },
      ],
    },
    {
      heading: "Go to",
      note: "G, then the letter.",
      rows: items
        .filter((item) => item.goKey)
        .map((item) => ({ label: item.label, keys: ["G", THEN, item.goKey!.toUpperCase()] })),
    },
    {
      heading: "Search",
      rows: [
        { label: "Move through the results", keys: ["↑", "↓"] },
        { label: "Open the one picked", keys: ["Enter"] },
      ],
    },
    {
      heading: "Lists",
      note: "Tab into the list first.",
      rows: [
        { label: "Walk the rows", keys: ["↑", "↓"] },
        { label: "Open the row", keys: ["Enter"] },
        { label: "Tick the row, in a list with tick boxes", keys: ["Space"] },
      ],
    },
  ];

  if (items.some((item) => QUEUES.includes(item.to))) {
    sections.push({
      heading: "Leave, letters and profile changes",
      note: "On the request the keyboard is on.",
      rows: [
        { label: "Approve", keys: ["A"] },
        { label: "Decline", keys: ["D"] },
      ],
    });
  }

  sections.push(
    {
      heading: "Charts",
      note: "Tab onto the chart first.",
      rows: [
        { label: "Read the point before or after", keys: ["←", "→"] },
        { label: "Read the first or the last point", keys: ["Home", "End"] },
        { label: "Put the readout away", keys: ["Esc"] },
      ],
    },
    {
      heading: "Dropdown lists",
      rows: [
        { label: "Move through the options", keys: ["↑", "↓"] },
        { label: "Jump to the first or the last", keys: ["Home", "End"] },
        { label: "Choose the one picked", keys: ["Enter"] },
      ],
    },
  );

  if (items.some((item) => item.to.endsWith("/chat"))) {
    sections.push({
      heading: "Messages",
      rows: [
        { label: "Send", keys: ["Enter"] },
        { label: "Start a new line", keys: ["Shift", "Enter"] },
      ],
    });
  }

  if (items.some((item) => NOTED.includes(item.to))) {
    sections.push({
      heading: "Notes on a record",
      rows: [{ label: "Save the note", keys: [commandKey(), "Enter"] }],
    });
  }

  return sections.filter((section) => section.rows.length > 0);
}

/**
 * The sheet of keyboard shortcuts.
 *
 * Opened with "?" anywhere outside a field, from the account menu, or from
 * the search box. It lists what the signed-in person can actually do: the
 * places G goes to come from the same list the sidebar is built from, and
 * the queue keys are left out for somebody who has no queues.
 */
export function ShortcutSheet({
  nav,
  canSearchPeople,
}: {
  nav: NavGroup[];
  canSearchPeople: boolean;
}) {
  const [open, setOpen] = useShortcutSheet();
  const sections = useMemo(() => sectionsFor(nav, canSearchPeople), [nav, canSearchPeople]);

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="Keyboard shortcuts"
      description="Single keys work outside a text field, while nothing is open over the page."
      size="lg"
    >
      <div className="gap-x-10 sm:columns-2">
        {sections.map((section) => (
          <section key={section.heading} className="mb-6 break-inside-avoid last:mb-0">
            <h3 className="text-2xs font-semibold text-fg-subtle">{section.heading}</h3>
            {section.note ? (
              <p className="mt-0.5 text-2xs text-fg-subtle">{section.note}</p>
            ) : null}
            <dl className="mt-1.5">
              {section.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-4 border-b border-line-faint py-1.5 last:border-b-0"
                >
                  <dt className="min-w-0 text-base text-fg">{row.label}</dt>
                  <dd className="flex shrink-0 items-center gap-1">
                    {row.keys.map((key, index) => (
                      <Fragment key={index}>
                        {typeof key === "string" ? (
                          <Kbd>{key}</Kbd>
                        ) : (
                          <span className="px-0.5 text-2xs text-fg-subtle">{key.say}</span>
                        )}
                      </Fragment>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
